
import { useEffect, useRef, useState } from 'react';

interface ServiceItemProps {
  service: {
    icon: string;
    title: string;
    description: string;
    image: string;
  };
  index: number;
}

function ServiceItem({ service, index }: ServiceItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (observer && ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`flex flex-col ${
        isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
      } gap-6 md:gap-8 items-center`}
    >
      <div
        className={`w-full lg:w-1/2 transition-all duration-1000 ${
          isVisible
            ? 'opacity-100 translate-x-0'
            : isEven
            ? 'opacity-0 -translate-x-10'
            : 'opacity-0 translate-x-10'
        }`}
      >
        <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-[250px] sm:h-[300px] md:h-[400px] object-cover object-top"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
      <div
        className={`w-full lg:w-1/2 transition-all duration-1000 delay-200 ${
          isVisible
            ? 'opacity-100 translate-x-0'
            : isEven
            ? 'opacity-0 translate-x-10'
            : 'opacity-0 -translate-x-10'
        }`}
      >
        <div className="w-12 h-12 md:w-16 md:h-16 bg-sky-100 rounded-full flex items-center justify-center mb-4 md:mb-6">
          <i className={`${service.icon} text-2xl md:text-3xl text-sky-600`}></i>
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">{service.title}</h3>
        <p className="text-sm md:text-lg text-gray-600 leading-relaxed">{service.description}</p>
      </div>
    </div>
  );
}

export default function CentervilleServices() {
  const services = [
    {
      icon: 'ri-flood-line',
      title: 'Water Damage Restoration in Centerville',
      description: 'When water damage strikes your Centerville home or business, immediate action is critical. Our water damage restoration team serves all Centerville neighborhoods, from properties near Deuel Creek to homes along Parrish Lane. We handle burst pipes, flooding, appliance leaks, and storm damage with advanced water extraction equipment and industrial-grade drying systems. Our Centerville water damage specialists understand the unique challenges of Utah\'s canyon-adjacent climate and respond 24/7 to minimize damage and prevent mold growth in your Centerville property.',
      image: 'https://public.readdy.ai/ai/img_res/8065482767b7434f8c0fb87b897b845b.jpg'
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Emergency Flood Cleanup Centerville',
      description: 'Centerville residents trust us for rapid emergency flood cleanup services throughout the city. Whether your property is near Parrish Creek or in the Centerville Heights area, our flood cleanup team arrives quickly with powerful extraction equipment. We remove standing water, dry affected areas, and restore your Centerville home to pre-flood condition. Our emergency flood cleanup process includes moisture detection, structural drying, and sanitization to protect your Centerville property from long-term water damage and ensure your family\'s safety.',
      image: 'https://readdy.ai/api/search-image?query=emergency%20flood%20water%20extraction%20in%20residential%20basement%2C%20professional%20team%20with%20industrial%20water%20pumps%20and%20vacuums%2C%20active%20cleanup%20operation%2C%20blue%20equipment%20and%20professional%20atmosphere&width=600&height=400&seq=centerville-service-002&orientation=landscape'
    },
    {
      icon: 'ri-plant-line',
      title: 'Mold Remediation Centerville Utah',
      description: 'Mold growth is a serious concern for Centerville homeowners, especially after water damage near canyon areas. Our certified mold remediation specialists serve all Centerville areas, from East Centerville to Creekside neighborhoods. We conduct thorough mold inspections, contain affected areas, and safely remove mold using EPA-approved methods. Our Centerville mold remediation service includes air quality testing and preventive treatments to keep your home safe. Don\'t let mold compromise your Centerville property — our team provides complete mold removal and prevention solutions.',
      image: 'https://readdy.ai/api/search-image?query=professional%20mold%20remediation%20specialist%20in%20protective%20gear%20inspecting%20wall%20with%20moisture%20meter%2C%20clean%20modern%20home%20interior%2C%20professional%20testing%20equipment%2C%20safety%20focused%20atmosphere&width=600&height=400&seq=centerville-service-003&orientation=landscape'
    },
    {
      icon: 'ri-fire-line',
      title: 'Fire Damage Repair Centerville',
      description: 'Fire damage requires specialized restoration expertise, and our Centerville team delivers comprehensive fire damage repair services. We serve Centerville properties from Deuel Creek to Pages Lane, handling smoke damage, soot removal, and structural repairs. Our fire damage restoration process includes odor elimination, content cleaning, and complete reconstruction when needed. Centerville families rely on us to restore their homes after fire disasters, and we work directly with insurance companies to streamline the claims process for Centerville residents.',
      image: 'https://readdy.ai/api/search-image?query=fire%20damage%20restoration%20professional%20cleaning%20smoke%20damaged%20walls%2C%20restoration%20equipment%20and%20cleaning%20supplies%2C%20professional%20service%20environment%20with%20blue%20accents%2C%20hope%20and%20recovery%20atmosphere&width=600&height=400&seq=centerville-service-004&orientation=landscape'
    },
    {
      icon: 'ri-thunderstorms-line',
      title: 'Storm Damage Restoration Centerville',
      description: 'Utah storms and canyon winds can cause significant damage to Centerville homes and businesses. Our storm damage restoration team responds quickly to properties throughout Centerville, from Deuel Creek to Parrish Lane and beyond. We handle wind damage, hail damage, fallen trees, roof damage, and flooding caused by severe weather. Our Centerville storm damage specialists provide emergency tarping, water extraction, structural repairs, and complete restoration services. We work directly with your insurance company to ensure your Centerville property is restored quickly and efficiently after any storm event.',
      image: 'https://readdy.ai/api/search-image?query=professional%20storm%20damage%20restoration%20team%20repairing%20residential%20roof%20after%20severe%20weather%2C%20workers%20with%20safety%20equipment%20on%20damaged%20home%20exterior%2C%20emergency%20repair%20services%2C%20overcast%20sky%20background&width=600&height=400&seq=centerville-service-005&orientation=landscape'
    },
    {
      icon: 'ri-windy-line',
      title: 'Odor Removal Services Centerville',
      description: 'Persistent odors can make your Centerville home uncomfortable and unhealthy. Our professional odor removal team serves all Centerville neighborhoods with advanced deodorization technology. We eliminate odors from smoke, fire, water damage, mold, pets, and other sources using hydroxyl generators, ozone treatments, and thermal fogging. Our Centerville odor removal specialists identify the source of the smell and treat it at the molecular level for permanent results. Whether your property is near Parrish Creek or in East Centerville, we restore fresh, clean air to your home or business.',
      image: 'https://readdy.ai/api/search-image?query=professional%20odor%20removal%20specialist%20using%20industrial%20air%20purification%20equipment%20in%20modern%20home%20interior%2C%20hydroxyl%20generator%20and%20air%20scrubber%20machines%2C%20clean%20fresh%20atmosphere%2C%20restoration%20service&width=600&height=400&seq=centerville-service-006&orientation=landscape'
    }
  ];

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            Our Centerville Home Restoration Services
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive disaster restoration services for Centerville homes and businesses
          </p>
        </div>

        <div className="space-y-10 md:space-y-16">
          {services.map((service, index) => (
            <ServiceItem key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
